import '@theme/fastutv-shopping-context';
import * as searchIntent from '@theme/fastutv-search-intent';
import { enhanceSupplierFacets } from '@theme/fastutv-supplier-facets';

const { buildSearchIntent, buildCandidateSearchQuery, displaySearchTerm, matchesSearchIntent, canonicalSupplier } = searchIntent;
const collator = new Intl.Collator(document.documentElement.lang || 'en', { numeric:true, sensitivity:'base' });
// Shopify already sorts ordinary paginated title/price/date results. Only supplier
// ordering needs a complete client-side set when no vehicle/category is selected.
const globalSorts = new Set(['vendor-ascending','vendor-descending']);
const localSorts = new Set([...globalSorts,'title-ascending','title-descending']);
const supportedNativeFilters = new Set(['filter.p.vendor','filter.p.product_type','filter.v.availability','filter.v.price.gte','filter.v.price.lte']);
const customParams = ['fastutv_sort','fastutv_supplier','fastutv_type','fastutv_available','fastutv_min','fastutv_max','fastutv_page'];
const CACHE_TTL=15*60*1000, CACHE_ROWS=12000, CACHE_SETS=6, CACHE_BYTES=32*1024*1024, CACHE_VERSION=4;
const completedSets=new Map();
const cacheStorageKey='fastutv-discovery-completed-v4';
let databasePromise;
let lastMetadataStart=-Infinity;
const wait = (ms, signal) => new Promise((resolve,reject) => {
  if(signal.aborted){reject(new DOMException('Aborted','AbortError'));return;}
  const onAbort=()=>{clearTimeout(timer);reject(new DOMException('Aborted','AbortError'));};
  const timer=setTimeout(()=>{signal.removeEventListener('abort',onAbort);resolve();},ms);
  signal.addEventListener('abort',onAbort,{once:true});
});
async function paceMetadata(signal) {
  // One sequential reader, capped at two starts per second. Slow responses
  // already provide the spacing; do not add another fixed delay after them.
  while(true){
    if(signal.aborted)throw new DOMException('Aborted','AbortError');
    const remaining=500-(performance.now()-lastMetadataStart);
    if(remaining<=0){lastMetadataStart=performance.now();return;}
    await wait(remaining,signal);
  }
}
function validEntry(entry,key=entry?.key) {
  return entry?.version===CACHE_VERSION&&entry.key===key&&Number.isFinite(entry.at)&&entry.at>0&&entry.at<=Date.now()+5000&&Date.now()-entry.at<=CACHE_TTL&&
    Array.isArray(entry.products)&&entry.products.length<=CACHE_ROWS&&entry.products.every(product=>product&&product.id!=null)&&
    entry.provenance&&Number.isFinite(entry.byteSize)&&entry.byteSize>0&&entry.byteSize<=CACHE_BYTES;
}
function database() {
  if(databasePromise)return databasePromise;
  databasePromise=new Promise(resolve=>{
    let settled=false;const done=value=>{if(!settled){settled=true;clearTimeout(timer);resolve(value);}else value?.close();};
    const timer=setTimeout(()=>done(null),800);
    try{
      const request=indexedDB.open(cacheStorageKey,1);
      request.onupgradeneeded=()=>{if(!request.result.objectStoreNames.contains('results'))request.result.createObjectStore('results',{keyPath:'key'});};
      request.onsuccess=()=>{const db=request.result;db.onversionchange=()=>db.close();done(db);};
      request.onerror=request.onblocked=()=>done(null);
    }catch{done(null);}
  });
  return databasePromise;
}
async function readStored(key) {
  const db=await database();if(!db)return null;
  return new Promise(resolve=>{
    let settled=false;const done=value=>{if(!settled){settled=true;clearTimeout(timer);resolve(value);}};
    const timer=setTimeout(()=>done(null),1200);
    try{const request=db.transaction('results','readonly').objectStore('results').get(key);request.onsuccess=()=>done(request.result);request.onerror=()=>done(null);}catch{done(null);}
  });
}
function trimMemory() {
  for(const [key,value] of completedSets)if(!validEntry(value,key))completedSets.delete(key);
  let rows=0,bytes=0;for(const value of completedSets.values()){rows+=value.products.length;bytes+=value.byteSize;}
  while(completedSets.size>CACHE_SETS||rows>CACHE_ROWS||bytes>CACHE_BYTES){const key=completedSets.keys().next().value,value=completedSets.get(key);rows-=value.products.length;bytes-=value.byteSize;completedSets.delete(key);}
}
async function cached(key) {
  let entry=completedSets.get(key);
  if(!validEntry(entry,key)){
    completedSets.delete(key);entry=await readStored(key);
    if(!validEntry(entry,key)){try{entry=JSON.parse(sessionStorage.getItem(cacheStorageKey)||'null');}catch{entry=null;}}
  }
  if(!validEntry(entry,key))return null;
  completedSets.delete(key);completedSets.set(key,entry);trimMemory();return entry;
}
async function remember(key,products,provenance,at=Date.now()) {
  if(products.length>CACHE_ROWS)return;
  const entry={version:CACHE_VERSION,key,at,products,provenance};
  let text;try{text=JSON.stringify(entry);entry.byteSize=new TextEncoder().encode(text).byteLength;}catch{return;}
  if(!validEntry(entry,key))return;
  completedSets.delete(key);completedSets.set(key,entry);trimMemory();
  // Small native sets also work when persistent storage is blocked. Complete
  // catalog pools exceed sessionStorage safely and use bounded IndexedDB only.
  try{if(text.length<=1500000)sessionStorage.setItem(cacheStorageKey,JSON.stringify(entry));else sessionStorage.removeItem(cacheStorageKey);}catch{/* Memory remains available. */}
  const db=await database();if(!db)return;
  await new Promise(resolve=>{
    let settled=false;const done=()=>{if(!settled){settled=true;clearTimeout(timer);resolve();}};let transaction;
    const timer=setTimeout(()=>{try{transaction?.abort();}catch{}done();},1500);
    try{
      transaction=db.transaction('results','readwrite');const store=transaction.objectStore('results'),request=store.getAll();
      request.onsuccess=()=>{
        const entries=request.result.filter(value=>value.key!==key&&validEntry(value)).concat(entry).sort((a,b)=>a.at-b.at);
        const keep=new Map(entries.map(value=>[value.key,value]));let rows=entries.reduce((n,value)=>n+value.products.length,0),bytes=entries.reduce((n,value)=>n+value.byteSize,0);
        while(keep.size>CACHE_SETS||rows>CACHE_ROWS||bytes>CACHE_BYTES){const oldest=keep.keys().next().value,value=keep.get(oldest);rows-=value.products.length;bytes-=value.byteSize;keep.delete(oldest);}
        for(const value of request.result)if(!keep.has(value.key))store.delete(value.key);
        if(keep.has(key))store.put(entry);
      };
      transaction.oncomplete=transaction.onerror=transaction.onabort=done;
    }catch{done();}
  });
}

function cappedVariantsCannotMatchVehicle(product,intent) {
  if(!(intent.model||intent.make)||!searchIntent.getMatchingVariants)return false;
  const vehicle={...intent,category:null,residual:''};
  if(matchesSearchIntent(product,vehicle))return false;
  const axes=product.options;
  if(!Array.isArray(axes)||!axes.length||axes.length>3)return false;
  let combinations=[''];
  for(let i=0;i<axes.length;i++){
    const axis=axes[i],values=axis?.values;
    if(!axis||axis.position!==i+1||!Array.isArray(values)||!values.length||values.some(value=>typeof value!=='string'||!value)||combinations.length>5000/values.length)return false;
    combinations=combinations.flatMap(before=>values.map(value=>before?before+' / '+value:value));
  }
  if(!Number.isInteger(product.variant_count)||combinations.length<product.variant_count)return false;
  const titles=new Set(combinations);
  // A nonstandard or incomplete option schema cannot certify exclusion. These
  // hypothetical combinations are never product/fitment or purchase evidence.
  if((product.variants||[]).some(variant=>typeof variant!=='string'||!titles.has(variant)))return false;
  const possible=combinations.map(title=>({title}));
  return searchIntent.getMatchingVariants({variants:possible},vehicle).length===0;
}

class FastUTVDiscovery extends HTMLElement {
  connectedCallback() {
    if(this.controller)return;
    try{this.config=JSON.parse(this.querySelector('[data-discovery-config]').textContent);}catch(error){console.warn('Search controls unavailable',error);return;}
    this.controller=new AbortController();this.s=this.config.strings;
    this.initialURL=new URL(location.href);this.url=new URL(location.href);
    this.readState();
    this.toggleAttribute('data-exact-pending',!!this.intent.scoped);
    // Older custom title-sort links used fastutv_sort without Shopify sort_by.
    // Convert those collection links to one ordinary native sorted page.
    if(this.config.kind==='collection'&&!this.needsCustomResults()&&localSorts.has(this.sort)&&this.url.searchParams.get('sort_by')!==this.sort&&this.config.native_sort!==this.sort){
      const next=new URL(this.url);next.searchParams.set('sort_by',this.sort);next.searchParams.delete('fastutv_sort');location.assign(next.href);return;
    }
    this.addEventListener('change',event=>this.change(event),{signal:this.controller.signal});
    this.addEventListener('click',event=>this.click(event),{signal:this.controller.signal});
    window.addEventListener('popstate',()=>this.restore(),{signal:this.controller.signal});
    this.setAttribute('data-enhanced','');
    if(this.needsCustomResults())this.activate();else this.showNative();
    enhanceSupplierFacets(this.closest('results-list')||document);
    this.observer=new MutationObserver(()=>{
      if(this.aliasFrame)return;
      this.aliasFrame=requestAnimationFrame(()=>{this.aliasFrame=0;if(this.isConnected)enhanceSupplierFacets(this.closest('results-list')||document);});
    });
    this.observer.observe(this.closest('results-list')||this,{childList:true,subtree:true});
  }

  readState() {
    this.raw=this.config.kind==='search'?displaySearchTerm(this.url):'';
    const rawIntent=buildSearchIntent(this.raw);
    const explicit=this.url.searchParams.get('fastutv_vehicle');
    const cleared=explicit==='all';
    this.intent=rawIntent;
    if(this.config.kind==='collection'){
      const tag=(this.config.tags||[]).find(value=>/^Browse Model:/i.test(value));
      this.intent=buildSearchIntent(cleared?'':explicit||this.config.vehicle||(tag?tag.replace(/^Browse Model:\s*/i,''):''));
    }else if(explicit&&!cleared&&!rawIntent.model&&!rawIntent.make&&!rawIntent.advanced){this.intent=buildSearchIntent(explicit+' '+this.raw);}
    this.sort=this.url.searchParams.get('fastutv_sort')||this.url.searchParams.get('sort_by')||this.config.native_sort;
    this.querySelector('[data-discovery-sort]').value=this.sort;
    const nativeSuppliers=this.url.searchParams.getAll('filter.p.vendor');
    this.state={
      supplier:this.url.searchParams.get('fastutv_supplier')||(nativeSuppliers.length?[...new Set(nativeSuppliers.map(canonicalSupplier))].join('|'):''),
      type:this.url.searchParams.get('fastutv_type')||this.url.searchParams.getAll('filter.p.product_type').join('|'),
      available:this.url.searchParams.get('fastutv_available')||(this.url.searchParams.getAll('filter.v.availability').length===1?this.url.searchParams.get('filter.v.availability'):'')||'',
      min:this.url.searchParams.get('fastutv_min')||this.url.searchParams.get('filter.v.price.gte')||'',
      max:this.url.searchParams.get('fastutv_max')||this.url.searchParams.get('filter.v.price.lte')||'',
      page:Math.max(1,Math.floor(Number(this.url.searchParams.get('fastutv_page'))||1))
    };
    if(this.raw&&this.config.kind==='search')document.title=this.s.search_title.replace('[query]',this.raw);
    const label=[this.intent.model?.label||this.intent.make?.label,this.intent.category?.label].filter(Boolean).join(' · ');
    const node=this.querySelector('[data-discovery-intent]');node.textContent=label;node.hidden=!label;
    const change=this.querySelector('[data-discovery-vehicle-change]');if(change)change.hidden=!(this.intent.model||this.intent.make);
    for(const [name,key] of [['availability','available'],['min','min'],['max','max']])this.querySelector('[data-discovery-'+name+']').value=this.state[key];
    this.querySelector('[data-discovery-fitment]').hidden=!this.intent.model;
    this.populateVehicles();
  }

  populateVehicles() {
    const choices=searchIntent.getVehicleChoices?.();if(!choices)return;
    const make=this.querySelector('[data-discovery-vehicle-make]'),model=this.querySelector('[data-discovery-vehicle-model]');if(!make||!model)return;
    const makeLabel=this.intent.model?.make||this.intent.make?.label||'';
    const selectedMake=choices.makes.find(item=>item.label===makeLabel);
    make.replaceChildren(new Option(this.s.all_vehicles||'All vehicles',''),...choices.makes.map(item=>new Option(item.label,item.id)));
    make.value=selectedMake?.id||'';
    model.replaceChildren(new Option(this.s.all_models||'All models',''),...choices.models.filter(item=>!makeLabel||item.make===makeLabel).map(item=>new Option(item.label,item.id)));
    model.value=this.intent.model?.id||'';
    model.disabled=!selectedMake;
    this.setAttribute('data-vehicle-ready','');
  }

  needsGlobalSort(sort=this.sort) {
    return globalSorts.has(sort)||(this.config.kind==='search'&&/^title-/.test(sort));
  }

  needsCustomResults() {
    return this.intent.scoped||this.needsGlobalSort()||customParams.some(key=>key!=='fastutv_sort'&&this.url.searchParams.has(key));
  }

  showNative() {
    if(this.blockedNative){this.blockedNative.hidden=false;this.blockedNative=null;}
    const filterLabel=this.querySelector('[data-discovery-filter-label]');if(filterLabel)filterLabel.textContent=this.s.vehicle||'Vehicle';
    this.fetchController?.abort();this.products=null;this.complete=false;this.setProvenance(null);
    for(const attr of ['data-active','data-results-ready','data-exact-pending','data-complete','aria-busy','data-error'])this.removeAttribute(attr);
    this.setAttribute('data-native-mode','');
    this.querySelector('[data-discovery-grid]').hidden=true;this.querySelector('[data-discovery-pages]').hidden=true;this.querySelector('[data-discovery-retry]').hidden=true;
    this.querySelector('[data-discovery-status]').textContent='';
    this.querySelector('[data-discovery-filters]').hidden=false;
    this.setControls(false);
    // A native vendor facet is complete only when its fresh counts account for
    // the entire unfiltered result set. Otherwise leave the native facets in use.
    const values=this.config.supplier_values||[];
    const unfiltered=![...this.url.searchParams.keys()].some(key=>key.startsWith('filter.'));
    const valid=unfiltered&&Number.isInteger(this.config.native_total)&&values.length&&values.reduce((sum,item)=>sum+Number(item.count||0),0)===this.config.native_total;
    if(valid){
      const counts=new Map();for(const item of values){const name=canonicalSupplier(item.label||item.value);counts.set(name,(counts.get(name)||0)+item.count);}
      const select=this.querySelector('[data-discovery-supplier]');select.replaceChildren(new Option(this.s.all_suppliers,''),...[...counts].sort(([a],[b])=>collator.compare(a,b)).map(([label,count])=>new Option(label+' ('+count+')',label)));select.disabled=false;
    }
    this.querySelector('[data-discovery-sort]').disabled=false;
  }

  setControls(ready) {
    for(const node of this.querySelectorAll('[data-discovery-supplier],[data-discovery-type],[data-discovery-availability],[data-discovery-min],[data-discovery-max],[data-discovery-clear]'))node.disabled=!ready;
    this.querySelector('[data-discovery-sort]').disabled=!ready;
  }

  disconnectedCallback() {
    if(this.blockedNative){this.blockedNative.hidden=false;this.blockedNative=null;}
    this.controller?.abort();this.fetchController?.abort();this.observer?.disconnect();if(this.aliasFrame)cancelAnimationFrame(this.aliasFrame);this.controller=null;
  }

  baseURL() {
    const base=new URL(this.url);base.hash='';base.searchParams.set('view','fastutv-discovery');
    const canonicalPrefixNone=!base.searchParams.has('options[prefix]')&&base.searchParams.getAll('options').length===1&&/^\{\s*"prefix"\s*=>\s*"none"\s*\}$/.test(base.searchParams.get('options'));
    if((this.intent.make||this.intent.model)&&!this.intent.advanced){
      const choices=searchIntent.getVehicleChoices?.();
      const known=new Set([...(choices?.makes||[]),...(choices?.models||[])].map(choice=>choice.tag.toLowerCase()));
      const handleize=value=>String(value).normalize('NFKD').replace(/[\u0300-\u036f]/g,'').toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'');
      const vehicleTags=new Set((this.config.tags||[]).filter(tag=>/^Browse (?:Make|Model):/i.test(tag)&&known.has(tag.toLowerCase())).map(handleize));
      const parts=base.pathname.replace(/\/$/,'').split('/'),index=parts.indexOf('collections');
      if(index>=0&&parts[index+1]&&parts[index+2]&&parts.length===index+3){
        const tags=parts[index+2].split('+'),kept=tags.filter(tag=>{try{return !vehicleTags.has(decodeURIComponent(tag));}catch{return true;}});
        if(kept.length!==tags.length)base.pathname=parts.slice(0,index+2).join('/')+(kept.length?'/'+kept.join('+'):'');
      }
    }
    // The all-products collection has no membership restriction to preserve.
    // Query its existing search view for scoped candidates rather than loading
    // the entire catalog, including products whose positive fitment is in a
    // title/variant/RPM field instead of a Browse Model tag.
    const scopedAll=this.config.kind==='collection'&&this.intent.scoped&&/\/collections\/all\/?$/.test(base.pathname)&&[...localSorts,'price-ascending','price-descending','relevance'].includes(this.sort);
    if(scopedAll)base.pathname=base.pathname.replace(/\/collections\/all\/?$/,'/search');
    for(const key of ['sections','section_id','page','options',...customParams])base.searchParams.delete(key);
    for(const key of supportedNativeFilters)base.searchParams.delete(key);
    for(const [key,value] of [['filter.v.price.gte',this.state.min],['filter.v.price.lte',this.state.max]])if(value!=='')base.searchParams.set(key,value);
    // Preserve one native ordering as the stable relevance/featured order. The
    // same complete set can then support supplier, title and price sorts locally.
    const nativeOrder=scopedAll?'relevance':!localSorts.has(this.sort)&&!['price-ascending','price-descending'].includes(this.sort)?this.sort:(this.config.kind==='search'?'relevance':this.config.native_sort);
    base.searchParams.set('sort_by',nativeOrder);
    if(this.config.kind==='search'||scopedAll){
      base.searchParams.set('type','product');
      // Shopify's large vehicle OR clause can broaden a combined keyword query.
      // Let native search own the residual words, then apply exact vehicle and
      // category guards locally. Never approximate body/stemming in a pool.
      // Combined vehicle/category queries can omit native matches inconsistently.
      // Load the bounded category set, then keep the same exact vehicle guards.
      const categoryVehicle=this.intent.category&&(this.intent.model||this.intent.make)&&!this.intent.residual&&!this.intent.advanced;
      const candidateIntent=categoryVehicle?{...this.intent,query:this.intent.category.query}:this.intent;
      base.searchParams.set('q',buildCandidateSearchQuery(candidateIntent));
      if(this.intent.scoped&&(!this.intent.residual||canonicalPrefixNone))base.searchParams.set('options[prefix]','none');
    }
    return base;
  }

  cacheKey(base) {
    const stable=new URL(base);stable.searchParams.sort();
    const identity=[document.documentElement.lang||'',globalThis.Shopify?.theme?.id||'',globalThis.Shopify?.currency?.active||this.config.currency||''];
    // Variant completion is scope-specific; do not reuse another vehicle's
    // category set before its matching variants have been fully inspected.
    if(/\/search\/?$/.test(base.pathname)&&this.intent.category&&!this.intent.residual&&!this.intent.advanced&&(this.intent.model||this.intent.make))identity.push('category-vehicle:'+(this.intent.model?.id||this.intent.make.id));
    return JSON.stringify(identity)+'|'+stable.href;
  }

  poolURL(base) {
    const fallback=new URL(base);if(!/\/search\/?$/.test(fallback.pathname))return null;
    fallback.pathname=fallback.pathname.replace(/\/search\/?$/,'/collections/all');
    for(const name of ['q','fastutv_q','type','fastutv_vehicle','options','options[prefix]','resources[type]','resources[limit_scope]','page'])fallback.searchParams.delete(name);
    fallback.searchParams.set('sort_by','title-ascending');return fallback;
  }

  async activate() {
    const filterLabel=this.querySelector('[data-discovery-filter-label]');if(filterLabel)filterLabel.textContent=this.s.filters||this.s.refine||'Filters';
    this.fetchController?.abort();this.nativeOrderLimited=false;this.setProvenance(null);const controller=new AbortController();this.fetchController=controller;
    this.setAttribute('data-active','');this.removeAttribute('data-native-mode');this.removeAttribute('data-error');this.removeAttribute('data-complete');
    this.setAttribute('aria-busy','true');this.complete=false;this.products=[];
    this.removeAttribute('data-results-ready');this.toggleAttribute('data-exact-pending',!!this.intent.scoped);
    this.querySelector('[data-discovery-status]').textContent=this.s.loading;this.querySelector('[data-discovery-retry]').hidden=true;
    this.querySelector('[data-discovery-filters]').hidden=false;this.setControls(false);
    this.querySelector('[data-discovery-grid]').hidden=true;this.querySelector('[data-discovery-pages]').hidden=true;
    const base=this.baseURL(),key=this.cacheKey(base);
    try {
      const searchSource=/\/search\/?$/.test(base.pathname);
      const poolURL=!this.intent.advanced&&this.intent.scoped&&!this.intent.residual?this.poolURL(base):null,poolKey=poolURL?'pool|'+this.cacheKey(poolURL):null;
      const savedPool=poolKey?await cached(poolKey):null;
      const saved=!savedPool?await cached(key):null;
      if(controller.signal.aborted||!this.isConnected)return;
      // Metadata cannot replay Shopify's body/stemming/advanced search semantics.
      // Ignore older residual-search fallbacks, while retaining valid native sets.
      const authoritativeSaved=!searchSource||this.intent.scoped&&!this.intent.residual||saved?.provenance.source==='native-search'&&saved.provenance.candidateTotal===saved.products.length&&saved.products.length<1000&&(!this.intent.scoped||!this.intent.residual||saved.provenance.candidateQuery===this.intent.residual)&&new Set(saved.products.map(product=>String(product.id))).size===saved.products.length;
      if(saved&&authoritativeSaved){this.finish(saved.products,controller,saved.provenance);return;}
      let seedIds=[],nativeSummary=null;
      const readPage=async(candidate,page)=>{
        const request=new URL(candidate);request.searchParams.set('page',String(page));
        await paceMetadata(controller.signal);
        const response=await fetch(request,{signal:controller.signal,credentials:'same-origin',headers:{Accept:'text/html'}});
        if(!response.ok)throw new Error('Native results HTTP '+response.status);
        const data=JSON.parse(await response.text());
        if(!Array.isArray(data.products)||!Number.isInteger(data.total)||!Number.isInteger(data.pages))throw new Error('Invalid native result response');
        return data;
      };
      const collect=async(candidate,isSearch=false)=>{
        const first=await readPage(candidate,1);
        if(first.page!==1||first.pages!==Math.max(1,Math.ceil(first.total/250)))throw new Error('Unexpected native pagination');
        if(first.total>25000||first.pages>100)throw new Error('Native result boundary exceeded');
        if(isSearch){seedIds=[...new Set(first.products.slice(0,250).map(product=>String(product.id)))];nativeSummary={url:candidate.href,total:first.total,pages:first.pages};}
        const unique=new Map();
        const accept=data=>{for(const product of data.products){
          if(unique.has(String(product.id)))throw Object.assign(new Error('Duplicate native product'),{fallbackReason:'duplicate-search'});
          unique.set(String(product.id),product);
        }};
        accept(first);this.preview([...unique.values()],controller,isSearch?'native-search':'native-collection');
        // A saturated native search does not prove the complete product set.
        // Keep its first safe batch as rank evidence, never as count closure.
        if(isSearch&&first.total>=1000)throw Object.assign(new Error('Native search result ceiling cannot establish completeness'),{fallbackReason:'saturated-search'});
        for(let page=2;page<=first.pages;page++){
          const data=await readPage(candidate,page);
          if(isSearch&&data.total>=1000)throw Object.assign(new Error('Native search result ceiling cannot establish completeness'),{fallbackReason:'saturated-search'});
          if(data.total!==first.total||data.pages!==first.pages||data.page!==page)throw Object.assign(new Error('Native results changed during loading'),isSearch?{fallbackReason:'changed-search'}:{});
          accept(data);this.preview([...unique.values()],controller,isSearch?'native-search':'native-collection');
        }
        if(unique.size!==first.total)throw Object.assign(new Error('Incomplete native result set'),isSearch?{fallbackReason:'incomplete-search'}:{});
        return {unique,total:first.total};
      };
      let collected,provenance={source:searchSource?'native-search':'native-collection',relevanceMode:'native-order',seedIds:[],candidateQuery:searchSource?base.searchParams.get('q'):null,nativeHTMLQuery:this.config.native_query??null};
      if(savedPool){
        const prior=savedPool.provenance.ranks?.find(value=>value.key===key)?.provenance;
        if(prior)provenance=prior;
        else{
          // The native first batch is rank evidence only. A completed pool can
          // cover another vehicle even when native search omitted that variant.
          const first=await readPage(base,1);
          if(first.page!==1||first.pages!==Math.max(1,Math.ceil(first.total/250)))throw new Error('Unexpected native pagination');
          seedIds=[...new Set(first.products.slice(0,250).map(product=>String(product.id)))];
          nativeSummary={url:base.href,total:first.total,pages:first.pages};this.preview(first.products,controller,'native-search');
          provenance={source:'collection-fallback',trigger:'complete-pool-reuse',relevanceMode:'native-first-batch-then-title',seedIds,nativeSearch:nativeSummary,candidateURL:poolURL.href};
        }
        const unique=new Map();for(const product of savedPool.products){if(unique.has(String(product.id)))throw new Error('Duplicate cached candidate');unique.set(String(product.id),product);}
        if(unique.size!==savedPool.provenance.candidateTotal)throw new Error('Incomplete cached candidate pool');
        collected={unique,total:unique.size};
      }else{
        try{collected=await collect(base,searchSource);}
        catch(error){
          // One alternative authoritative source, not a retry of a failed HTTP
          // request. Broad/advanced search semantics cannot be evaluated locally.
          if(!searchSource||!this.intent.scoped||this.intent.residual||this.intent.advanced||!error.fallbackReason)throw error;
          const fallback=this.poolURL(base);
          provenance={source:'collection-fallback',trigger:error.fallbackReason,relevanceMode:'native-first-batch-then-title',seedIds,nativeSearch:nativeSummary,candidateURL:fallback.href};
          this.setProvenance(provenance);
          collected=await collect(fallback);
        }
      }
      const unique=collected.unique;provenance.candidateTotal=collected.total;
      if(controller.signal.aborted||!this.isConnected)return;
      for(const product of unique.values()){
        // Positive evidence can show a safe early card; absence in a capped
        // variant list cannot exclude a product until all native pages are read.
        if((this.intent.model||this.intent.make)&&product.variant_count>(product.variants||[]).length&&!matchesSearchIntent(product,{...this.intent,category:null,residual:''})&&!cappedVariantsCannotMatchVehicle(product,this.intent)){
          const variantURL=new URL(product.url,location.origin);if(variantURL.origin!==location.origin)throw new Error('Invalid product origin');
          variantURL.searchParams.set('view','fastutv-discovery');const preview=this.url.searchParams.get('preview_theme_id');if(preview)variantURL.searchParams.set('preview_theme_id',preview);
          const variants=new Map();let page=1,pages=1;
          do{
            await wait(500,controller.signal);variantURL.searchParams.set('page',String(page));
            await paceMetadata(controller.signal);
            const response=await fetch(variantURL,{signal:controller.signal,credentials:'same-origin'});if(!response.ok)throw new Error('Native variant results HTTP '+response.status);
            const data=JSON.parse(await response.text());
            if(data.total!==product.variant_count||data.page!==page||data.pages!==Math.max(1,Math.ceil(data.total/50))||!Array.isArray(data.variants)||data.pages>50)throw new Error('Incomplete native variant metadata');
            pages=data.pages;for(const variant of data.variants)variants.set(String(variant.id),variant);page++;
          }while(page<=pages);
          if(variants.size!==product.variant_count)throw new Error('Incomplete native variant set');
          product.variant_details=[...variants.values()];product.variants=product.variant_details.map(variant=>variant.title);product.variant_prices=product.variant_details.map(variant=>variant.price);
        }
      }
      if(controller.signal.aborted||!this.isConnected)return;
      const products=[...unique.values()];
      if(poolKey&&provenance.source==='collection-fallback'){
        const ranks=(savedPool?.provenance.ranks||[]).filter(value=>value.key!==key).concat({key,provenance}).slice(-CACHE_SETS);
        await remember(poolKey,products,{source:'candidate-pool',candidateURL:poolURL.href,candidateTotal:products.length,ranks},savedPool?.at);
      }else await remember(key,products,provenance);
      this.finish(products,controller,provenance);
    }catch(error){
      if(error.name==='AbortError'||controller.signal.aborted)return;
      if(/\/search\/?$/.test(base.pathname)&&(!this.intent.scoped||this.intent.residual)&&error.fallbackReason){
        this.restoreNativeOrder();
        this.dispatchEvent(new CustomEvent('fastutv:discovery-error',{bubbles:true,detail:{message:error.message,partial:!!this.products?.length}}));
        return;
      }
      this.setAttribute('data-error','');this.querySelector('[data-discovery-status]').textContent=this.products?.length?(this.s.partial_error||this.s.error):this.s.error;
      this.querySelector('[data-discovery-retry]').hidden=false;
      this.dispatchEvent(new CustomEvent('fastutv:discovery-error',{bubbles:true,detail:{message:error.message,partial:!!this.products?.length}}));
    }finally{if(this.fetchController===controller)this.removeAttribute('aria-busy');}
  }

  candidates(products,source) {
    // Native search already accepted body-only, stemming and advanced matches.
    // Native queries enforce residual words; vehicle/category guards stay exact.
    // Variant selection still uses the full intent, including its residual.
    return source==='native-search'?(!this.intent.scoped?products:products.filter(product=>matchesSearchIntent(product,{...this.intent,residual:''}))):products.filter(product=>matchesSearchIntent(product,this.intent));
  }

  restoreNativeOrder() {
    const native=this.initialURL.searchParams;
    const values=(params,key,transform=value=>value)=>[...new Set(params.getAll(key).map(transform))].sort().join('|');
    const availability=native.getAll('filter.v.availability');
    const initial={supplier:values(native,'filter.p.vendor',canonicalSupplier),type:values(native,'filter.p.product_type'),available:availability.length===1?availability[0]:'',min:native.get('filter.v.price.gte')||'',max:native.get('filter.v.price.lte')||''};
    const supported=Object.keys(initial).every(key=>String(this.state[key]).split('|').sort().join('|')===initial[key]);
    const signature=url=>{
      const copy=new URL(url);copy.hash='';
      for(const key of [...customParams,'sort_by','page',...supportedNativeFilters])copy.searchParams.delete(key);
      copy.searchParams.sort();return copy.href;
    };
    // The saved native DOM reflects the original server filters, not later
    // client-only filters. Reveal it only when those states are equivalent.
    const original=!this.intent.scoped&&supported&&signature(this.url)===signature(this.initialURL);
    const sort=original?this.config.native_sort:this.baseURL().searchParams.get('sort_by');
    if(original)this.showNative();
    else if(!this.hasAttribute('data-results-ready')){const native=this.closest('results-list')?.querySelector('.collection-wrapper');if(native&&!native.hidden){this.blockedNative=native;native.hidden=true;}}
    this.sort=sort;this.querySelector('[data-discovery-sort]').value=sort;
    const next=new URL(this.url);next.searchParams.delete('fastutv_sort');next.searchParams.delete('fastutv_page');next.searchParams.set('sort_by',sort);
    history.replaceState({},'',next);this.url=next;
    this.nativeOrderLimited=true;this.complete=false;this.removeAttribute('data-complete');this.setAttribute('data-error','');
    this.querySelector('[data-discovery-status]').textContent=(this.s.native_sort_limit||this.s.error+' '+this.s.empty_help).replace('[sort]',this.querySelector('[data-discovery-sort]').selectedOptions[0]?.textContent||sort);
    this.querySelector('[data-discovery-retry]').hidden=true;
    // Clearing client filters is always safe and can restore native browsing.
    if(!original)this.querySelector('[data-discovery-clear]').disabled=false;
  }

  preview(products,controller,source) {
    if(controller.signal.aborted||!this.isConnected)return;
    const safe=this.candidates(products,source);
    // Keep the first useful page stable while subsequent native pages load.
    if(!this.hasAttribute('data-results-ready')&&safe.length){this.products=safe;this.render(false);}
  }

  setProvenance(provenance) {
    this.provenance=provenance;
    this.nativeSeedRank=new Map((provenance?.seedIds||[]).map((id,index)=>[String(id),index]));
    const hybrid=provenance?.relevanceMode==='native-first-batch-then-title';
    this.toggleAttribute('data-hybrid-relevance',hybrid);
    const option=this.querySelector('[data-discovery-sort] option[value="relevance"]');
    if(option){option.dataset.nativeLabel??=option.textContent;option.textContent=hybrid&&this.s.best_match?this.s.best_match:option.dataset.nativeLabel;}
  }

  finish(products,controller,provenance) {
    if(controller.signal.aborted||!this.isConnected)return;
    this.setProvenance(provenance);
    this.products=this.candidates(products,provenance?.source);this.loadedKey=this.cacheKey(this.baseURL());this.complete=true;this.setControls(true);this.render(true);
  }

  displayProduct(product) {
    if(!(this.intent.model||this.intent.make)||!searchIntent.getMatchingVariants)return product;
    const details=product.variant_details||[];
    if(!Number.isInteger(product.variant_count)||details.length!==product.variant_count)return product;
    const variants=searchIntent.getMatchingVariants({...product,variants:details},this.intent);
    const unique=[...new Map(variants.filter(variant=>variant&&variant.id!=null).map(variant=>[String(variant.id),variant])).values()];
    // Several fuel/tune/seat choices are still a shopper decision. Never choose
    // the first matching option or invent a price/image for an unknown variant.
    if(unique.length!==1)return product;
    const variant=unique[0];if(!variant.url||!variant.price_text)return product;
    const url=new URL(variant.url,location.origin);if(url.origin!==location.origin||url.searchParams.get('variant')!==String(variant.id))return product;
    return {...product,url:url.pathname+url.search,price:variant.price,price_text:variant.price_text,price_varies:false,available:variant.available,
      image:variant.image||product.image,image_alt:variant.image_alt||product.image_alt,image_width:variant.image_width||product.image_width,image_height:variant.image_height||product.image_height};
  }

  priceMatches(product) {
    const min = this.state.min === '' ? -Infinity : Number(this.state.min)*100;
    const max = this.state.max === '' ? Infinity : Number(this.state.max)*100;
    const selected=this.displayProduct(product);
    if(selected!==product)return Number(selected.price)>=min&&Number(selected.price)<=max;
    if(product.variant_count > (product.variant_prices || []).length)return true;
    return (product.variant_prices || [product.price]).some(price => Number(price) >= min && Number(price) <= max);
  }

  filtered(ignore) {
    return this.products.filter(product =>
      (ignore === 'supplier' || !this.state.supplier || this.state.supplier.split('|').includes(canonicalSupplier(product.vendor))) &&
      (ignore === 'type' || !this.state.type || this.state.type.split('|').includes(product.product_type)) &&
      (!this.state.available || String(Number(this.displayProduct(product).available)) === this.state.available) &&
      this.priceMatches(product));
  }

  fillOptions(selector, options, empty, selected) {
    const select = this.querySelector(selector);
    const counts = new Map();
    for (const value of options) if (value) counts.set(value,(counts.get(value)||0)+1);
    if (selected && !counts.has(selected)) counts.set(selected, selected.split('|').reduce((sum,value)=>sum+(counts.get(value)||0),0));
    const nodes = [new Option(empty,'')];
    for (const [value,count] of [...counts].sort(([a],[b])=>collator.compare(a,b))) nodes.push(new Option(value.split('|').join(' + ')+' ('+count+')',value));
    select.replaceChildren(...nodes);
    select.value = selected;
  }

  render(complete = this.complete) {
    if (complete) {
    this.fillOptions('[data-discovery-supplier]',this.filtered('supplier').map(product=>canonicalSupplier(product.vendor)),this.s.all_suppliers,this.state.supplier);
    this.fillOptions('[data-discovery-type]',this.filtered('type').map(product=>product.product_type),this.s.all_parts,this.state.type);
    }
    const rows = this.filtered();
    const titleOrder = (a,b) => collator.compare(a.title,b.title) || String(a.id).localeCompare(String(b.id));
    if (complete && (this.sort === 'vendor-ascending' || this.sort === 'vendor-descending')) {
      const direction = this.sort.endsWith('descending') ? -1 : 1;
      rows.sort((a,b)=>direction*collator.compare(canonicalSupplier(a.vendor),canonicalSupplier(b.vendor)) || titleOrder(a,b));
    } else if (complete && (this.sort === 'title-ascending' || this.sort === 'title-descending')) {
      rows.sort((a,b)=>(this.sort.endsWith('descending')?-1:1)*titleOrder(a,b));
    } else if (complete && (this.sort === 'price-ascending' || this.sort === 'price-descending')) {
      rows.sort((a,b)=>(this.sort.endsWith('descending')?-1:1)*(this.displayProduct(a).price-this.displayProduct(b).price)||titleOrder(a,b));
    } else if (complete && this.sort==='relevance' && this.provenance?.relevanceMode==='native-first-batch-then-title') {
      // Local best match: retain only the first native batch's verified rank,
      // followed by every other exact match in title order. This is deliberately
      // not represented as Shopify's full relevance ranking.
      rows.sort((a,b)=>(this.nativeSeedRank.get(String(a.id))??Infinity)-(this.nativeSeedRank.get(String(b.id))??Infinity)||titleOrder(a,b));
    }
    const pages = Math.max(1,Math.ceil(rows.length/24));
    if (complete) this.state.page = Math.min(this.state.page,pages);
    const page = complete ? this.state.page : 1;
    const subset = rows.slice((page-1)*24,page*24);
    this.querySelector('[data-discovery-status]').textContent = complete ? (rows.length ? this.s.count.replace('[count]',rows.length) : this.s.empty+' '+this.s.empty_help) : this.s.partial_loading || this.s.loading;
    const inherited=(this.config.native_filters||[]).filter(filter=>!supportedNativeFilters.has(filter.param)&&this.url.searchParams.has(filter.param)).flatMap(filter=>(filter.active||[]).map(value=>filter.label+': '+value.label));
    const inheritedNode=this.querySelector('[data-discovery-native-filters]');
    if(inheritedNode){inheritedNode.textContent=inherited.join(' · ');inheritedNode.hidden=!inherited.length;}
    this.querySelector('[data-discovery-grid]').replaceChildren(...subset.map(product=>this.card(product)));
    const nav = this.querySelector('[data-discovery-pages]');
    nav.hidden = !complete || pages<=1;
    const buttons = [];
    const add = (label,page,current=false) => {
      const button=document.createElement('button');
      button.type='button'; button.textContent=label; button.dataset.discoveryPage=page;
      if(current) button.setAttribute('aria-current','page');
      buttons.push(button);
    };
    if(this.state.page>1)add(this.s.previous,this.state.page-1);
    const choices=new Set([1,pages,this.state.page-1,this.state.page,this.state.page+1]);
    for(const page of [...choices].sort((a,b)=>a-b)) if(page>=1&&page<=pages)add(String(page),page,page===this.state.page);
    if(this.state.page<pages)add(this.s.next,this.state.page+1);
    nav.replaceChildren(...buttons);
    this.setAttribute('data-results-ready','');
    this.removeAttribute('data-exact-pending');
    this.toggleAttribute('data-complete',complete);
    this.querySelector('[data-discovery-grid]').hidden=false;
  }

  card(product) {
    product=this.displayProduct(product);
    const li=document.createElement('li'); li.className='fastutv-discovery__card'; li.dataset.productId=product.id; li.dataset.supplier=canonicalSupplier(product.vendor); li.dataset.price=product.price;
    const link=document.createElement('a');
    const url=new URL(product.url,location.origin);
    if(url.origin!==location.origin) return li;
    link.href=url.pathname+url.search;
    const media=document.createElement('div'); media.className='fastutv-discovery__image';
    if(product.image){
      const img=document.createElement('img');img.src=product.image;img.alt=product.image_alt||product.title;img.width=product.image_width;img.height=product.image_height;img.loading='lazy';img.decoding='async';
      try {
        const source=new URL(product.image,location.origin);
        if(source.origin===location.origin&&source.pathname.startsWith('/cdn/shop/')) {
          img.srcset=[240,360,480,720].map(width=>{const candidate=new URL(source);candidate.searchParams.set('width',String(width));return candidate.href+' '+width+'w';}).join(', ');
          img.sizes='auto, (min-width: 1440px) 329px, (min-width: 1101px) calc((100vw - 124px) / 4), (min-width: 750px) calc((100vw - 104px) / 3), calc((100vw - 44px) / 2)';
        }
      } catch { /* Keep the native image URL as the fallback. */ }
      media.append(img);
    }
    link.append(media);
    if(product.vendor){const vendor=document.createElement('p');vendor.className='fastutv-discovery__vendor';vendor.textContent=canonicalSupplier(product.vendor);link.append(vendor);}
    const heading=document.createElement('h3');heading.className='fastutv-discovery__title';heading.textContent=product.title;link.append(heading);
    const price=document.createElement('p');price.className='fastutv-discovery__price-text';price.textContent=(product.price_varies?this.s.from+' ':'')+product.price_text;link.append(price);
    const availability=document.createElement('p');availability.className='fastutv-discovery__availability';availability.textContent=product.available?this.s.in_stock:this.s.unavailable;link.append(availability);
    li.append(link); return li;
  }

  change(event) {
    if(event.target.matches('[data-discovery-vehicle-make],[data-discovery-vehicle-model]')){this.changeVehicle(event.target);return;}
    if(event.target.matches('[data-discovery-sort]')){
      this.sort=event.target.value;
      if(!this.hasAttribute('data-active')&&!this.needsGlobalSort()){
        const next=new URL(location.href);next.searchParams.set('sort_by',this.sort);next.searchParams.delete('page');next.searchParams.delete('fastutv_sort');location.assign(next.href);return;
      }
      this.state.page=1;this.save();
      if(!this.complete){this.activate();return;}
      if(!localSorts.has(this.sort)&&!['price-ascending','price-descending'].includes(this.sort)&&this.loadedKey!==this.cacheKey(this.baseURL())){this.activate();return;}
      this.render();return;
    }
    const mapping=[['supplier','supplier'],['type','type'],['availability','available'],['min','min'],['max','max']];
    for(const [attribute,key] of mapping)if(event.target.matches('[data-discovery-'+attribute+']')){
      this.state[key]=event.target.value;this.state.page=1;
      if(!this.hasAttribute('data-active')){
        const next=new URL(this.url);next.searchParams.delete('page');
        if(key==='supplier'){
          next.searchParams.delete('filter.p.vendor');
          for(const item of this.config.supplier_values||[])if(canonicalSupplier(item.label||item.value)===this.state.supplier)next.searchParams.append('filter.p.vendor',item.value||item.label);
        }else{const param={type:'filter.p.product_type',available:'filter.v.availability',min:'filter.v.price.gte',max:'filter.v.price.lte'}[key];next.searchParams.delete(param);if(this.state[key]!=='')next.searchParams.set(param,this.state[key]);}
        location.assign(next.href);return;
      }
      this.save();if(key==='min'||key==='max')this.activate();else if(this.complete)this.render();break;
    }
  }

  changeVehicle(target) {
    const choices=searchIntent.getVehicleChoices?.();if(!choices)return;
    const modelControl=target.matches('[data-discovery-vehicle-model]');
    const choice=modelControl?(choices.models.find(item=>item.id===target.value)||choices.makes.find(item=>item.id===this.querySelector('[data-discovery-vehicle-make]').value)):choices.makes.find(item=>item.id===target.value);
    const label=choice?(choice.make?choice.make+' '+choice.label:choice.label):'';
    const next=new URL(this.url);next.searchParams.delete('page');next.searchParams.delete('fastutv_page');
    next.searchParams.set('fastutv_vehicle',label||'all');
    if(this.config.kind==='collection'){
      const segments=next.pathname.split('/').filter(Boolean),index=segments.indexOf('collections');
      if(index>=0&&segments[index+1]){
        const handle=decodeURIComponent(segments[index+1]);
        const handleIntent=buildSearchIntent(handle.replace(/-/g,' '));
        const titleIntent=buildSearchIntent(this.dataset.label||this.config.label||'');
        const baseIntent=handleIntent.model||handleIntent.make?handleIntent:titleIntent;
        const sameFamily=baseIntent.model&&choice?.make&&(choice.familyTag||choice.tag)===(baseIntent.model.familyTag||baseIntent.model.tag);
        const sameMake=baseIntent.make&&choice&&(choice.make||choice.label)===baseIntent.make.label;
        if((baseIntent.model&&!sameFamily)||(baseIntent.make&&!sameMake))segments[index+1]='all';
        const tags=(this.config.tags||[]).filter(tag=>!/^Browse (?:Make|Model):/i.test(tag));
        const handleize=value=>String(value).normalize('NFKD').replace(/[\u0300-\u036f]/g,'').toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'');
        next.pathname='/'+segments.slice(0,index+2).join('/')+(tags.length?'/'+tags.map(handleize).join('+'):'');
        if(next.pathname!==this.url.pathname){
          this.dispatchEvent(new CustomEvent('fastutv:vehicle-change',{bubbles:true,detail:{label,url:next.href}}));location.assign(next.href);return;
        }
      }
    }
    if(this.config.kind==='search'&&(this.intent.model||this.intent.make)&&searchIntent.replaceSearchVehicle){
      const raw=searchIntent.replaceSearchVehicle(this.raw,label);next.searchParams.set('q',raw);next.searchParams.delete('fastutv_q');
    }
    history.pushState({},'',next);this.url=next;this.readState();
    if(this.needsCustomResults())this.activate();else this.showNative();
    this.dispatchEvent(new CustomEvent('fastutv:vehicle-change',{bubbles:true,detail:{label,url:next.href}}));
  }

  click(event) {
    if(event.target.closest('[data-discovery-vehicle-change]')){
      const details=this.querySelector('.fastutv-discovery__filter-disclosure');if(details)details.open=true;
      this.querySelector(this.intent.model?'[data-discovery-vehicle-model]':'[data-discovery-vehicle-make]')?.focus();return;
    }
    if(event.target.closest('[data-discovery-retry]')){this.activate();return;}
    if(event.target.closest('[data-discovery-clear]')){
      const hadPrice=!!this.state.min||!!this.state.max;
      Object.assign(this.state,{supplier:'',type:'',available:'',min:'',max:'',page:1});
      for(const name of ['availability','min','max'])this.querySelector('[data-discovery-'+name+']').value='';
      const refetch=hadPrice||[...this.url.searchParams.keys()].some(key=>key.startsWith('filter.')&&!supportedNativeFilters.has(key));
      this.clearNative=true;this.save();if(this.nativeOrderLimited){const next=new URL(this.url);for(const key of customParams)next.searchParams.delete(key);location.assign(next.href);return;}if(refetch)this.activate();else if(this.complete)this.render();return;
    }
    const button=event.target.closest('[data-discovery-page]');
    if(button&&this.complete){
      this.state.page=Number(button.dataset.discoveryPage);this.save();this.render();
      const status=this.querySelector('[data-discovery-status]');status.setAttribute('tabindex','-1');status.focus({preventScroll:true});status.scrollIntoView({block:'start',behavior:'auto'});
    }
  }

  restore() {
    this.url=new URL(location.href);this.readState();
    if(this.needsCustomResults()||this.config.kind==='search'&&this.loadedKey&&this.url.searchParams.has('fastutv_sort'))this.activate();else this.showNative();
  }

  save() {
    const next=new URL(this.url);next.searchParams.delete('page');
    for(const key of customParams)next.searchParams.delete(key);
    next.searchParams.set('fastutv_sort',this.sort);if(!globalSorts.has(this.sort))next.searchParams.set('sort_by',this.sort);
    for(const [key,param] of [['supplier','fastutv_supplier'],['type','fastutv_type'],['available','fastutv_available'],['min','fastutv_min'],['max','fastutv_max'],['page','fastutv_page']]){
      const value=this.state[key];if(value!==''&&!(key==='page'&&value===1))next.searchParams.set(param,String(value));
    }
    for(const key of [...next.searchParams.keys()])if(supportedNativeFilters.has(key)||(this.clearNative&&key.startsWith('filter.')))next.searchParams.delete(key);
    this.clearNative=false;history.pushState({},'',next);this.url=next;
  }
}
if(!customElements.get('fastutv-discovery'))customElements.define('fastutv-discovery',FastUTVDiscovery);
